import { useState, useEffect, useId, useRef, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion, useInView, animate } from 'framer-motion';
import {
  Tv, Zap, ArrowRight, CheckCircle2, Sparkles,
  ShoppingCart, Pill, UtensilsCrossed, Dumbbell, Dog, Wrench, Package, Boxes,
  Wifi, RefreshCw, Megaphone, TrendingUp, Wallet, BadgeCheck, LayoutGrid,
  Play, X, ChevronDown, ChevronLeft, ChevronRight, Quote, Beef, Apple, Beer,
  Croissant, Eye, MessageCircle, DollarSign, Clock, Tag, Rocket, BarChart3,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { MyShowLogoFull } from '@/components/Logo';

/* ============================================================================
   MyShow — Homepage de alta conversão
   Posicionamento: NÃO vende software. Vende "transformar TVs em vendedores".
   Ordem do funil: Desejo → Problema → Solução → Benefícios → Prova → Cadastro.
   Foco em RESULTADO (vender mais), nunca em funcionalidade.

   Mockups em CSS (placeholders premium) — estruturados com `image`/`videoUrl`
   opcionais para plugar fotos/vídeo reais sem retrabalho.
============================================================================ */

const SIGNUP = '/cadastro';
const WHATSAPP = 'https://wa.me/554699826101';

/* Plugue o vídeo real aqui (TV funcionando dentro de um supermercado).
   Vazio = usa o mockup animado em CSS. */
const HERO_VIDEO_URL = '';
const DEMO_VIDEO_URL = '';

/* Foto real da loja no hero (full-bleed por baixo do texto HTML).
   Vazio = usa o mockup animado de TV em CSS. */
const HERO_IMAGE_URL = '/hero-loja.webp';

/* ---------- Reveal on scroll ---------- */
function Reveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const prefersReduced = useReducedMotion();
  if (prefersReduced) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

function SectionTag({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block text-xs uppercase tracking-[0.25em] text-brand-700 font-semibold mb-3">
      {children}
    </span>
  );
}

/* ============================================================================
   CENÁRIOS DE TV
============================================================================ */
type Scene = {
  segment: string;
  icon: React.ComponentType<{ className?: string }>;
  tag: string;
  tagColor: string;
  product: string;
  price: string;
  old?: string;
  accent: string;
  image?: string; // futuro: arte/foto real
};

const heroScenes: Scene[] = [
  { segment: 'Supermercado', icon: ShoppingCart, tag: 'OFERTA DO DIA', tagColor: 'text-red-300 bg-red-500/15 border-red-500/40',
    product: 'Picanha Maturada', price: 'R$ 39,90', old: 'R$ 59,90', accent: 'from-red-300 via-orange-400 to-amber-500' },
  { segment: 'Farmácia', icon: Pill, tag: 'QUEDA DE PREÇO', tagColor: 'text-emerald-300 bg-emerald-500/15 border-emerald-500/40',
    product: 'Vitamina C 1g', price: 'R$ 19,90', old: 'R$ 34,90', accent: 'from-emerald-300 via-teal-400 to-cyan-500' },
  { segment: 'Loja & Utilidades', icon: Package, tag: 'LANÇAMENTO', tagColor: 'text-violet-300 bg-violet-500/15 border-violet-500/40',
    product: 'Air Fryer 5L Digital', price: 'R$ 299', old: 'R$ 459', accent: 'from-violet-300 via-fuchsia-400 to-purple-500' },
];

function TVContent({ scene, compact = false }: { scene: Scene; compact?: boolean }) {
  const Icon = scene.icon;
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center text-center text-white px-6"
      style={{ background: 'radial-gradient(120% 80% at 50% 110%, rgba(168,85,247,.35) 0%, transparent 60%), linear-gradient(180deg, #0b0d12, #111827)' }}
    >
      <div className={`inline-flex items-center gap-1.5 border ${scene.tagColor} font-bold tracking-[0.18em] rounded-full mb-3 ${compact ? 'text-[7px] px-2 py-0.5' : 'text-[10px] px-2.5 py-1'}`}>
        <Sparkles className={compact ? 'h-2 w-2' : 'h-3 w-3'} /> {scene.tag}
      </div>
      <div className={`flex items-center gap-2 font-display font-bold tracking-tight ${compact ? 'text-sm' : 'text-2xl md:text-3xl'}`}>
        <Icon className={compact ? 'h-3.5 w-3.5 opacity-70' : 'h-6 w-6 opacity-70'} />
        {scene.product}
      </div>
      {!compact && <div className="mt-3 mb-1 text-[10px] uppercase tracking-[0.3em] text-white/50">Por apenas</div>}
      <div className={`font-display font-black bg-gradient-to-br ${scene.accent} bg-clip-text text-transparent ${compact ? 'text-3xl mt-1' : 'text-6xl md:text-7xl'}`}>
        {scene.price}
      </div>
      {scene.old && <div className={`text-white/40 line-through ${compact ? 'text-[10px] mt-0.5' : 'text-sm mt-1'}`}>De {scene.old}</div>}
      {!compact && <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-white/40">Válido só hoje · enquanto durar o estoque</div>}
      <div aria-hidden className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 120px rgba(0,0,0,0.55)' }} />
    </div>
  );
}

/* TV grande do hero — cicla cenários (ou mostra vídeo real se HERO_VIDEO_URL) */
function HeroTV() {
  const prefersReduced = useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (prefersReduced || HERO_VIDEO_URL) return;
    const t = setInterval(() => setI((p) => (p + 1) % heroScenes.length), 3600);
    return () => clearInterval(t);
  }, [prefersReduced]);
  const scene = heroScenes[i];

  return (
    <div className="relative">
      <div aria-hidden className="absolute -inset-10 bg-gradient-to-br from-brand-500/40 via-fuchsia-500/30 to-purple-700/40 blur-3xl opacity-60 rounded-full" />

      <div className="absolute -top-4 -left-6 z-20 float-chip animate-float-y" style={{ animationDelay: '.4s' }}>
        <span className="live-dot" /> <span className="text-foreground">{HERO_VIDEO_URL ? 'Sua loja, ao vivo' : scene.segment}</span>
      </div>
      <div className="absolute -top-2 right-2 z-20 float-chip animate-float-y" style={{ animationDelay: '1.1s' }}>
        <Wifi className="h-3.5 w-3.5 text-emerald-500" /> <span className="text-foreground">Atualizado agora</span>
      </div>
      <div className="absolute -bottom-3 -right-4 z-20 float-chip animate-float-y" style={{ animationDelay: '1.8s' }}>
        <Zap className="h-3.5 w-3.5 text-brand-600" /> <span className="text-foreground">Trocou no painel, mudou na TV</span>
      </div>

      <div className="tv-bezel rounded-[1.8rem] p-3 relative z-10 rotate-[0.6deg]">
        <div className="flex justify-center pb-2"><div className="h-1 w-12 rounded-full bg-white/10" /></div>
        <div className="tv-screen rounded-[1.2rem] aspect-video relative overflow-hidden">
          <div className="absolute top-3 left-4 right-4 flex justify-between text-[10px] text-white/60 tracking-wider z-10">
            <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> AO VIVO</span>
            <span>MYSHOW</span>
          </div>
          {HERO_VIDEO_URL ? (
            <video src={HERO_VIDEO_URL} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline />
          ) : (
            <AnimatePresence mode="wait">
              <motion.div key={i} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.02 }} transition={{ duration: 0.5, ease: 'easeOut' }} className="absolute inset-0">
                <TVContent scene={scene} />
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>

      <div className="relative z-10 flex flex-col items-center -mt-1">
        <div className="tv-stand h-3 w-24 rounded-b-md" />
        <div className="tv-stand h-2 w-40 rounded-md mt-0.5" />
      </div>
      <div aria-hidden className="absolute left-1/2 -translate-x-1/2 mt-4 w-[80%] h-10 rounded-[50%]" style={{ background: 'radial-gradient(ellipse at center, rgba(168,85,247,.25), transparent 70%)', filter: 'blur(6px)' }} />

      {!HERO_VIDEO_URL && (
        <div className="relative z-10 flex justify-center gap-1.5 mt-6">
          {heroScenes.map((_, idx) => (
            <span key={idx} className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? 'w-6 bg-brand-600' : 'w-1.5 bg-border'}`} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================================
   DADOS
============================================================================ */
/* Números fortes logo abaixo do hero (o cérebro do empresário reage a número) */
const heroMetrics = [
  { icon: Eye, kpi: 'até 10x', label: 'mais visibilidade para suas promoções' },
  { icon: Zap, kpi: 'segundos', label: 'para atualizar qualquer campanha' },
  { icon: LayoutGrid, kpi: '1 painel', label: 'para controlar todas as suas lojas' },
];

/* Credibilidade (vai na prova social) */
const credStats = [
  { kpi: '+27%', label: 'no ticket médio' },
  { kpi: '< 15 min', label: 'do cadastro à TV ligada' },
  { kpi: '24 anos', label: 'de varejo nas costas' },
  { kpi: '99,5%', label: 'de disponibilidade' },
];

/* Benefícios EMOCIONAIS — verbos no imperativo, foco no ganho */
const benefits = [
  { icon: Eye, title: 'Faça suas promoções serem vistas', desc: 'Tela em movimento rouba o olhar onde o cartaz de papel é ignorado. Sua oferta não passa batida por ninguém.' },
  { icon: TrendingUp, title: 'Transforme quem passa em quem compra', desc: 'O cliente vê, deseja e leva junto. Produto certo, na tela certa, bem na hora da decisão.' },
  { icon: RefreshCw, title: 'Altere ofertas instantaneamente, sem imprimir nada', desc: 'Mudou o preço? Aparece em segundos em todas as telas. Adeus gráfica, papel e canetão.' },
  { icon: Wallet, title: 'Pare de gastar com cartaz toda semana', desc: 'O que você joga fora em impresso todo mês paga o MyShow — e ainda sobra.' },
  { icon: BadgeCheck, title: 'Dê à sua loja a cara de uma grande rede', desc: 'Comunicação visual profissional que transmite confiança e faz o cliente comprar mais.' },
  { icon: LayoutGrid, title: 'Controle todas as lojas em poucos cliques', desc: 'Uma ou cinquenta lojas: você comanda todas as telas de um lugar só — do PC ou do celular.' },
];

const steps = [
  { n: 1, icon: Tv, t: 'Cadastre suas telas', d: 'Qualquer TV com internet vira sua mídia. Pareamento em segundos, sem técnico.' },
  { n: 2, icon: Megaphone, t: 'Escolha ou crie campanhas', d: 'Use um template pronto ou monte a sua arrastando e soltando. Fácil como mexer no celular.' },
  { n: 3, icon: Zap, t: 'Publique na hora', d: 'Um clique e a promoção está no ar em todas as telas. Mudou de ideia? Troca na hora.' },
];

/* "Seu cliente compra com os olhos" — carrossel de setores (foto real + texto HTML) */
type CarouselSeg = {
  key: string;
  badge: string;
  badgeIcon: React.ComponentType<{ className?: string }>;
  highlight: string;
  subtitle: string;
  bullets: { icon: React.ComponentType<{ className?: string }>; text: string }[];
  chip: { icon: React.ComponentType<{ className?: string }>; text: string };
  image: string;
  tone: { text: string; border: string; icon: string; dot: string; glow: string };
};

const carouselSegments: CarouselSeg[] = [
  {
    key: 'acougue', badge: 'Açougue', badgeIcon: Beef,
    highlight: 'cortes que vendem mais',
    subtitle: 'Mostre ofertas, cortes especiais e promoções do dia de forma irresistível no momento da compra.',
    bullets: [
      { icon: Eye, text: 'Mais atenção no balcão' },
      { icon: ShoppingCart, text: 'Mais compras por impulso' },
      { icon: BarChart3, text: 'Mais valor no ticket médio' },
      { icon: DollarSign, text: 'Mais resultado para sua loja' },
    ],
    chip: { icon: Tag, text: 'Promoções que o cliente vê, o cliente compra.' },
    image: '/loja-acougue.webp',
    tone: { text: 'text-rose-300', border: 'border-rose-400/40', icon: 'text-rose-400', dot: 'bg-rose-400', glow: 'rgba(244,63,94,0.35)' },
  },
  {
    key: 'hortifruti', badge: 'Hortifruti', badgeIcon: Apple,
    highlight: 'o que está fresquinho',
    subtitle: 'Frutas, verduras e ofertas do dia que mudam de preço em segundos — sem cartaz, sem caneta.',
    bullets: [
      { icon: Eye, text: 'Mais visibilidade' },
      { icon: RefreshCw, text: 'Preço sempre certo' },
      { icon: ShoppingCart, text: 'Mais vendas por impulso' },
      { icon: DollarSign, text: 'Mais resultado' },
    ],
    chip: { icon: Sparkles, text: 'O cliente vê fresquinho na tela e leva na hora.' },
    image: '/loja-hortifruti.webp',
    tone: { text: 'text-lime-300', border: 'border-lime-400/40', icon: 'text-lime-400', dot: 'bg-lime-400', glow: 'rgba(132,204,22,0.35)' },
  },
  {
    key: 'bebidas', badge: 'Bebidas', badgeIcon: Beer,
    highlight: 'ofertas geladas',
    subtitle: 'Atraia atenção para produtos de alta margem e promoções sazonais.',
    bullets: [
      { icon: Eye, text: 'Mais visibilidade' },
      { icon: RefreshCw, text: 'Mais giro de estoque' },
      { icon: TrendingUp, text: 'Mais vendas' },
      { icon: DollarSign, text: 'Mais resultado' },
    ],
    chip: { icon: Rocket, text: 'Promoções que o cliente vê, o cliente compra.' },
    image: '/loja-bebidas.webp',
    tone: { text: 'text-violet-300', border: 'border-violet-400/40', icon: 'text-violet-400', dot: 'bg-violet-400', glow: 'rgba(139,92,246,0.4)' },
  },
  {
    key: 'padaria', badge: 'Padaria', badgeIcon: Croissant,
    highlight: 'ofertas que dão água na boca',
    subtitle: 'Promoções irresistíveis no momento certo, estimulando compras por impulso todos os dias.',
    bullets: [
      { icon: Eye, text: 'Mais visibilidade' },
      { icon: ShoppingCart, text: 'Mais vendas por impulso' },
      { icon: RefreshCw, text: 'Mais giro de produtos' },
      { icon: DollarSign, text: 'Mais resultado' },
    ],
    chip: { icon: Rocket, text: 'A oferta certa no lugar certo faz o cliente levar mais.' },
    image: '/loja-padaria.webp',
    tone: { text: 'text-amber-300', border: 'border-amber-400/40', icon: 'text-amber-400', dot: 'bg-amber-400', glow: 'rgba(245,158,11,0.35)' },
  },
  {
    key: 'farmacia', badge: 'Farmácia', badgeIcon: Pill,
    highlight: 'promoções que cuidam e vendem',
    subtitle: 'Atualize ofertas em segundos e comunique campanhas com clareza, sem depender de impressão.',
    bullets: [
      { icon: Clock, text: 'Atualização em tempo real' },
      { icon: Tag, text: 'Campanhas segmentadas' },
      { icon: Eye, text: 'Mais visibilidade' },
      { icon: ShoppingCart, text: 'Mais conversão' },
      { icon: BarChart3, text: 'Mais resultado' },
    ],
    chip: { icon: Zap, text: 'Menos esforço. Mais impacto. Mais vendas.' },
    image: '/loja-farmacia.webp',
    tone: { text: 'text-indigo-300', border: 'border-indigo-400/40', icon: 'text-indigo-400', dot: 'bg-indigo-400', glow: 'rgba(99,102,241,0.4)' },
  },
];

const segments = [
  { icon: ShoppingCart, name: 'Supermercados', slug: 'supermercados', desc: 'Promoções em tempo real' },
  { icon: Pill, name: 'Farmácias', slug: 'farmacias', desc: 'Medicamentos + beleza' },
  { icon: Boxes, name: 'Atacados', slug: 'atacados', desc: 'Mix e preço por volume' },
  { icon: UtensilsCrossed, name: 'Restaurantes', slug: 'restaurantes', desc: 'Menu digital + combos' },
  { icon: Dog, name: 'Pet Shops', slug: 'petshops', desc: 'Banho, tosa e produtos' },
  { icon: Dumbbell, name: 'Academias', slug: 'academias', desc: 'Planos e novidades' },
  { icon: Package, name: 'Utilidades', slug: 'utilidades', desc: 'Lançamentos + ofertas' },
  { icon: Wrench, name: 'Materiais de Construção', slug: 'construcao', desc: 'Ofertas por setor' },
];

/* PLACEHOLDER — substituir por depoimentos REAIS aprovados antes de publicar. */
const testimonials = [
  { quote: 'Tirei o cartaz de papel e o canetão da loja. As imagens dos produtos na TV vendem muito mais — e ninguém mais erra preço.', author: 'Conafri Mais', region: 'Laranjeiras do Sul — PR' },
  { quote: 'O cliente entra pra comprar uma panela e sai com o jogo completo. A oferta certa na tela, na hora certa — o ticket médio subiu de verdade.', author: 'Shoplar', region: 'Quedas do Iguaçu — PR' },
  { quote: 'Enquanto o cliente espera no balcão, a TV vende sozinha. Eles já chegam no caixa decididos.', author: 'Farmavida', region: 'Quedas do Iguaçu — PR' },
];

const faqs = [
  { q: 'Preciso comprar uma Smart TV nova?', a: 'Não. Funciona em qualquer TV com entrada HDMI + internet. Se já for Smart TV (Android TV, Fire TV ou LG webOS), nem precisa de aparelho extra. Se não for, um TV Box baratinho resolve.' },
  { q: 'Funciona com Android TV e Fire TV?', a: 'Sim. Temos aplicativo nativo para Android TV, Fire TV e LG webOS. É só instalar, parear com o código de 8 dígitos e pronto.' },
  { q: 'Preciso instalar ou configurar algo complicado?', a: 'Não. Você cria a conta, monta a campanha no painel (arrastar e soltar) e parear a TV leva segundos. Da inscrição à primeira tela no ar costuma levar menos de 15 minutos.' },
  { q: 'Consigo controlar várias lojas de um lugar só?', a: 'Sim. No painel você gerencia quantas telas e lojas quiser, do computador ou do celular. Ideal para redes e franquias — muda tudo de uma vez ou loja por loja.' },
  { q: 'Tem fidelidade ou multa de cancelamento?', a: 'Não tem fidelidade. Você testa grátis, e se continuar paga mês a mês. Cancela quando quiser, sem multa.' },
  { q: 'Quanto tempo leva para começar a usar?', a: 'O teste gratuito é liberado na hora, sem cartão de crédito. Em poucos minutos você já publica sua primeira campanha.' },
];

/* ============================================================================
   OPORTUNIDADE FINANCEIRA — "Quanto vale uma compra por impulso?"
   Logo após a Hero. NÃO é calculadora: é uma história financeira que faz o
   dono sentir o dinheiro que está deixando passar pela porta.
============================================================================ */
function brl(n: number) {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}

/* Número que conta sozinho ao entrar na tela (respeita prefers-reduced-motion) */
function CountValue({ to, format, className }: { to: number; format: (n: number) => string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const prefersReduced = useReducedMotion();
  const [n, setN] = useState(0);
  useEffect(() => {
    if (prefersReduced) { setN(to); return; }
    if (!inView) return;
    const controls = animate(0, to, { duration: 1.6, ease: [0.22, 1, 0.36, 1], onUpdate: setN });
    return () => controls.stop();
  }, [inView, to, prefersReduced]);
  return <span ref={ref} className={className}>{format(n)}</span>;
}

/* Os 3 ingredientes da conta — apresentados como fatos, não como sliders */
const oppCards = [
  { emoji: '👥', value: '100', unit: 'clientes por dia', glow: 'rgba(168,85,247,.5)',
    desc: 'Fluxo normal de uma loja de pequeno ou médio porte.' },
  { emoji: '🛒', value: '20', unit: 'clientes impactados', glow: 'rgba(217,70,239,.5)',
    desc: 'Clientes que visualizaram uma promoção em uma TV do MyShow.' },
  { emoji: '💰', value: 'R$ 10', unit: 'a mais por compra', glow: 'rgba(245,158,11,.5)',
    desc: 'Valor médio adicional gerado por uma compra impulsionada.' },
];

/* Ilustração: a decisão de compra acontecendo — TV → produto → carrinho */
function DecisionFlow() {
  const prefersReduced = useReducedMotion();
  return (
    <div className="relative h-full rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.015] p-7 md:p-9 overflow-hidden">
      <div aria-hidden className="absolute inset-0 bg-dots opacity-[0.06]" />
      <div aria-hidden className="absolute -top-20 -right-16 w-56 h-56 rounded-full bg-brand-500/20 blur-3xl" />

      <div className="relative flex items-center justify-between gap-3 sm:gap-6">
        {/* TV exibindo a oferta */}
        <div className="shrink-0 w-[40%] max-w-[12rem]">
          <div className="tv-bezel rounded-2xl p-1.5">
            <div className="tv-screen rounded-xl aspect-video relative overflow-hidden flex flex-col items-center justify-center text-center px-2">
              <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[8px] text-white/60 tracking-wider">
                <span className="h-1 w-1 rounded-full bg-emerald-400 animate-pulse" /> AO VIVO
              </span>
              <span className="text-[7px] font-bold tracking-[0.18em] text-red-300 bg-red-500/15 border border-red-500/40 rounded-full px-1.5 py-0.5">OFERTA DO DIA</span>
              <span className="mt-1 font-display font-black text-lg bg-gradient-to-br from-red-300 via-orange-400 to-amber-500 bg-clip-text text-transparent leading-none">R$ 39,90</span>
              <span className="text-[8px] text-white/40 line-through">R$ 59,90</span>
            </div>
          </div>
          <div className="mt-2 h-1.5 w-10 mx-auto rounded-b bg-white/10" />
          <p className="mt-2 text-center text-[11px] text-background/60">A oferta aparece</p>
        </div>

        {/* Rail da decisão: produto viajando da TV para o carrinho */}
        <div className="relative flex-1 h-20">
          <div aria-hidden className="absolute top-1/2 left-0 right-0 -translate-y-1/2 step-connector h-0.5 opacity-70" />
          <Sparkles className="absolute top-0 left-1/2 -translate-x-1/2 h-3.5 w-3.5 text-brand-300/80" />
          {prefersReduced ? (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 grid place-items-center h-9 w-9 rounded-xl bg-white text-brand-700 shadow-glow">
              <Package className="h-5 w-5" />
            </div>
          ) : (
            <motion.div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 grid place-items-center h-9 w-9 rounded-xl bg-white text-brand-700 shadow-glow"
              initial={{ left: '6%', opacity: 0, scale: 0.6 }}
              animate={{ left: ['6%', '30%', '70%', '94%'], opacity: [0, 1, 1, 0], scale: [0.6, 1, 1, 0.7] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut', times: [0, 0.18, 0.82, 1] }}
            >
              <Package className="h-5 w-5" />
            </motion.div>
          )}
          <p className="absolute -bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap text-[11px] font-medium text-brand-200">momento da decisão</p>
        </div>

        {/* Carrinho + cliente: a venda entra */}
        <div className="shrink-0 text-center">
          <div className="relative grid place-items-center h-20 w-20 rounded-2xl bg-gradient-to-br from-brand-500/30 to-fuchsia-500/20 border border-white/10">
            <ShoppingCart className="h-9 w-9 text-white" strokeWidth={2} />
            <span className="absolute -top-2 -right-2 inline-flex items-center rounded-full gradient-brand text-white text-[10px] font-bold px-2 py-0.5 shadow-glow">+1</span>
            {!prefersReduced && (
              <motion.span aria-hidden className="absolute inset-0 rounded-2xl ring-2 ring-brand-400/60"
                animate={{ opacity: [0, 0.8, 0], scale: [0.9, 1.15, 1.25] }}
                transition={{ duration: 2.8, repeat: Infinity, ease: 'easeOut', times: [0.7, 0.88, 1] }} />
            )}
          </div>
          <p className="mt-2 text-[11px] text-background/60">entra no carrinho</p>
        </div>
      </div>

      <div className="relative mt-7 flex items-center gap-2.5 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
        <Eye className="h-4 w-4 text-brand-300 shrink-0" />
        <p className="text-sm text-background/80">O cliente vê, deseja e leva — bem na hora em que decide o que vai no carrinho.</p>
      </div>
    </div>
  );
}

function OportunidadeFinanceira() {
  const dia = 200, mes = 6000, ano = 72000;
  return (
    <>
      {/* Os 3 fatos */}
      <div className="grid sm:grid-cols-3 gap-5 md:gap-6">
        {oppCards.map((c, i) => (
          <Reveal key={c.value + c.unit} delay={i * 0.12}>
            <div className="group relative h-full rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.015] p-7 md:p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-white/25">
              <div aria-hidden className="absolute -top-px left-1/2 -translate-x-1/2 h-px w-2/3" style={{ background: `linear-gradient(90deg, transparent, ${c.glow}, transparent)` }} />
              <div className="grid place-items-center h-16 w-16 rounded-2xl text-3xl bg-white/5 border border-white/10 transition-transform duration-300 group-hover:scale-110"
                style={{ boxShadow: `0 12px 40px -12px ${c.glow}` }}>
                <span>{c.emoji}</span>
              </div>
              <div className="mt-6 font-display text-4xl md:text-5xl font-black tracking-tight text-white leading-none">{c.value}</div>
              <div className="mt-1.5 text-sm font-semibold uppercase tracking-wider text-brand-200">{c.unit}</div>
              <p className="mt-4 text-sm text-background/65 leading-relaxed">{c.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Resultado (números que param o dono) + ilustração da decisão */}
      <div className="mt-10 md:mt-12 grid lg:grid-cols-2 gap-6 items-stretch">
        <Reveal>
          <div className="relative h-full overflow-hidden rounded-3xl gradient-brand text-white p-8 md:p-10 flex flex-col justify-center text-center shadow-glow">
            <div aria-hidden className="absolute inset-0 bg-dots opacity-15" />
            <div aria-hidden className="absolute -top-20 -right-16 w-64 h-64 rounded-full bg-white/15 blur-3xl" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/80">
                <TrendingUp className="h-4 w-4" /> A mais no seu caixa, todo mês
              </span>
              <div className="mt-3 font-display font-black tracking-tight tabular-nums leading-none text-6xl md:text-8xl">
                + <CountValue to={mes} format={brl} />
              </div>
              <div className="mt-8 grid grid-cols-2 divide-x divide-white/20 border-t border-white/20 pt-6">
                <div className="px-2">
                  <div className="font-display text-2xl md:text-3xl font-black tabular-nums leading-none">+ <CountValue to={dia} format={brl} /></div>
                  <div className="mt-1.5 text-xs uppercase tracking-[0.2em] text-white/70">por dia</div>
                </div>
                <div className="px-2">
                  <div className="font-display text-2xl md:text-3xl font-black tabular-nums leading-none">+ <CountValue to={ano} format={brl} /></div>
                  <div className="mt-1.5 text-xs uppercase tracking-[0.2em] text-white/70">por ano</div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <DecisionFlow />
        </Reveal>
      </div>
    </>
  );
}

/* ============================================================================
   FAQ
============================================================================ */
function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="border border-border/70 rounded-2xl bg-card overflow-hidden transition-shadow hover:shadow-soft">
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={id}
        className="w-full flex items-center justify-between gap-4 text-left px-5 md:px-6 py-5 font-semibold tracking-tight">
        {q}
        <ChevronDown className={`h-5 w-5 shrink-0 text-brand-600 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div id={id} className="grid transition-all duration-300 ease-out" style={{ gridTemplateRows: open ? '1fr' : '0fr' }}>
        <div className="overflow-hidden">
          <p className="px-5 md:px-6 pb-5 text-muted-foreground leading-relaxed">{a}</p>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
   Modal de demonstração
============================================================================ */
function DemoModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!open || DEMO_VIDEO_URL) return;
    const t = setInterval(() => setI((p) => (p + 1) % heroScenes.length), 2800);
    return () => clearInterval(t);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[60] grid place-items-center p-4 bg-foreground/70 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
          role="dialog" aria-modal="true" aria-label="Demonstração do MyShow">
          <motion.div className="relative w-full max-w-3xl" initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} onClick={(e) => e.stopPropagation()}>
            <button onClick={onClose} aria-label="Fechar" className="absolute -top-3 -right-3 z-10 h-10 w-10 grid place-items-center rounded-full bg-white text-foreground shadow-elev hover:scale-105 transition-transform">
              <X className="h-5 w-5" />
            </button>
            <div className="tv-bezel rounded-[1.4rem] p-2.5">
              <div className="tv-screen rounded-[1rem] aspect-video relative overflow-hidden">
                <div className="absolute top-3 left-4 right-4 flex justify-between text-[10px] text-white/60 tracking-wider z-10">
                  <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> AO VIVO</span>
                  <span>MYSHOW · DEMO</span>
                </div>
                {DEMO_VIDEO_URL ? (
                  <video src={DEMO_VIDEO_URL} className="absolute inset-0 w-full h-full object-cover" autoPlay controls playsInline />
                ) : (
                  <AnimatePresence mode="wait">
                    <motion.div key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="absolute inset-0">
                      <TVContent scene={heroScenes[i]} />
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>
            </div>
            <div className="mt-5 text-center">
              <p className="text-white/90 text-sm mb-4">É exatamente assim que sua TV aparece para o cliente — trocando a oferta sozinha, em tempo real.</p>
              <Button asChild size="lg" variant="gradient" className="btn-gradient-glow h-12 px-7">
                <Link to={SIGNUP}>🔥 Transformar minha TV em vendedor <ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ============================================================================
   HERO COPY — reutilizado no layout com foto (dark) e no fallback animado (light)
============================================================================ */
function HeroCopy({ dark, onDemo }: { dark: boolean; onDemo: () => void }) {
  return (
    <>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className={`inline-flex max-w-full items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs font-semibold mb-6 shadow-soft backdrop-blur leading-snug ${dark ? 'border-white/25 bg-white/10 text-white' : 'border-brand-200 bg-brand-50/80 text-brand-800'}`}>
        Marketing indoor que vende — sem cartaz, sem caneta
      </motion.div>

      <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
        className={`font-display text-[2.5rem] leading-[1.05] sm:text-5xl md:text-[3.8rem] md:leading-[1.03] font-extrabold tracking-tight text-balance [overflow-wrap:break-word] ${dark ? 'text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.4)]' : 'text-foreground'}`}>
        Transforme suas TVs em{' '}
        <span className="relative inline-block">
          <span className={dark ? 'bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent' : 'text-gradient-brand'}>máquinas de vender mais.</span>
          <svg aria-hidden viewBox="0 0 320 14" className={`absolute -bottom-2 left-0 w-full h-3 ${dark ? 'text-amber-400/80' : 'text-brand-500/70'}`} preserveAspectRatio="none">
            <path d="M2 9 C 90 1, 230 1, 318 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" fill="none" />
          </svg>
        </span>
      </motion.h1>

      <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
        className={`mt-6 text-lg md:text-xl max-w-xl leading-relaxed ${dark ? 'text-white/85' : 'text-muted-foreground'}`}>
        Destaque ofertas, aumente compras por impulso e atualize todas as telas da sua loja{' '}
        <strong className={dark ? 'text-white' : 'text-foreground'}>em segundos</strong>.
      </motion.p>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
        className="mt-9 flex flex-col sm:flex-row sm:flex-wrap gap-3">
        <Button asChild size="lg" variant="gradient" className="btn-gradient-glow cta-pulse w-full sm:w-auto px-8 text-base font-semibold" style={{ height: '3.25rem' }}>
          <Link to={SIGNUP}>🚀 Criar minha primeira TV agora</Link>
        </Button>
        <Button size="lg" variant="outline" onClick={onDemo}
          className={`w-full sm:w-auto px-7 text-base backdrop-blur ${dark ? 'bg-white/10 text-white border-white/40 hover:bg-white/20 hover:text-white' : 'bg-background/70 border-border hover:border-foreground/30'}`}
          style={{ height: '3.25rem' }}>
          <Play className="h-4 w-4" /> Ver como funciona
        </Button>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.35 }}
        className={`mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm ${dark ? 'text-white/80' : 'text-muted-foreground'}`}>
        <span className="flex gap-1.5 items-center"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Sem cartão de crédito</span>
        <span className="flex gap-1.5 items-center"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Configuração rápida</span>
        <span className="flex gap-1.5 items-center"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Comece em minutos</span>
      </motion.div>
    </>
  );
}

/* ============================================================================
   SEGMENT CAROUSEL — troca sozinho, com setas e indicadores; cor por setor
============================================================================ */
function SegmentCarousel() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = carouselSegments.length;
  const go = (d: number) => setI((p) => (p + d + n) % n);

  // Troca automática a cada 6s (sempre — só pausa ao passar o mouse)
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((p) => (p + 1) % n), 4000);
    return () => clearInterval(t);
  }, [paused, n]);

  const s = carouselSegments[i];

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {/* Slides empilhados na mesma célula de grid → altura constante (a do maior), sem pulo nem corte */}
      <div
        className="grid rounded-3xl overflow-hidden ring-1 ring-white/10 bg-zinc-950 transition-shadow duration-500"
        style={{ boxShadow: `0 40px 100px -30px ${s.tone.glow}` }}
      >
        {carouselSegments.map((seg, idx) => {
          const SegBadge = seg.badgeIcon;
          const active = idx === i;
          return (
            <div
              key={seg.key}
              aria-hidden={!active}
              className={`col-start-1 row-start-1 relative transition-opacity duration-500 ${active ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            >
              {/* Foto do setor (direita no desktop, fundo no mobile) */}
              <img
                src={seg.image}
                alt={`MyShow no setor de ${seg.badge} — TVs exibindo ofertas`}
                className="absolute inset-y-0 right-0 h-full w-full md:w-[60%] object-cover object-center"
                loading="lazy"
                decoding="async"
              />
              <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/20 md:via-zinc-950/75 md:to-transparent" />

              {/* Painel de texto */}
              <div className="relative p-8 sm:p-10 md:p-12 lg:p-14 max-w-lg">
                <span className={`inline-flex max-w-full items-center gap-2 rounded-full border ${seg.tone.border} ${seg.tone.text} bg-white/5 px-3.5 py-1.5 text-xs font-bold uppercase tracking-widest`}>
                  <SegBadge className="h-4 w-4 shrink-0" /> {seg.badge}
                </span>
                <h3 className="mt-5 font-display text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
                  Destaque<br />
                  <span className={seg.tone.text}>{seg.highlight}</span>
                </h3>
                <p className="mt-4 text-white/70 leading-relaxed max-w-md">{seg.subtitle}</p>
                <ul className="mt-6 space-y-0">
                  {seg.bullets.map((b) => (
                    <li key={b.text} className="flex items-center gap-3 text-white/90 text-sm border-b border-white/10 py-2.5 last:border-0">
                      <b.icon className={`h-4 w-4 shrink-0 ${seg.tone.icon}`} /> {b.text}
                    </li>
                  ))}
                </ul>
                <div className={`mt-6 inline-flex items-center gap-2 rounded-xl border ${seg.tone.border} bg-white/5 px-4 py-3 text-sm text-white/90`}>
                  <seg.chip.icon className={`h-4 w-4 shrink-0 ${seg.tone.icon}`} /> {seg.chip.text}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Controles */}
      <div className="flex items-center justify-between mt-6">
        <div className="flex gap-2">
          {carouselSegments.map((seg, idx) => (
            <button
              key={seg.key}
              onClick={() => setI(idx)}
              aria-label={`Ver ${seg.badge}`}
              aria-current={idx === i}
              className={`h-2 rounded-full transition-all duration-300 ${idx === i ? `w-8 ${seg.tone.dot}` : 'w-2 bg-white/25 hover:bg-white/40'}`}
            />
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={() => go(-1)} aria-label="Setor anterior" className="grid place-items-center h-11 w-11 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={() => go(1)} aria-label="Próximo setor" className="grid place-items-center h-11 w-11 rounded-full gradient-brand text-white hover:opacity-90 transition-opacity">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ============================================================================
   CARROSSEL DE CAMPANHAS — TVs reais em ambientes reais (uso, não "templates")
============================================================================ */
const campaignSlides = [
  { key: 'acougue', emoji: '🥩', segment: 'Açougue', line: 'Destaque cortes premium e promoções do dia.', image: '/loja-acougue.webp' },
  { key: 'hortifruti', emoji: '🥭', segment: 'Hortifruti', line: 'Transforme frutas e verduras em ofertas irresistíveis.', image: '/loja-hortifruti.webp' },
  { key: 'padaria', emoji: '🥖', segment: 'Padaria', line: 'Aumente compras por impulso na padaria.', image: '/loja-padaria.webp' },
  { key: 'bebidas', emoji: '🍺', segment: 'Bebidas', line: 'Destaque ofertas geladas e produtos de alta margem.', image: '/loja-bebidas.webp' },
  { key: 'farmacia', emoji: '🍼', segment: 'Farmácia', line: 'Atualize campanhas instantaneamente sem trocar cartazes.', image: '/loja-farmacia.webp' },
];

function CampaignCarousel() {
  const prefersReduced = useReducedMotion();
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = campaignSlides.length;
  const go = (d: number) => setI((p) => (p + d + n) % n);

  // Autoplay suave (pausa no hover / respeita reduce-motion)
  useEffect(() => {
    if (paused || prefersReduced) return;
    const t = setInterval(() => setI((p) => (p + 1) % n), 4500);
    return () => clearInterval(t);
  }, [paused, prefersReduced, n]);

  return (
    <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {/* Slides empilhados + cross-fade (robusto, à prova de reduce-motion) */}
      <div className="group relative rounded-[1.75rem] overflow-hidden ring-1 ring-border/70 shadow-elev bg-zinc-950 aspect-[16/11] sm:aspect-[16/9] lg:aspect-[2.3/1]">
        {campaignSlides.map((slide, k) => {
          const active = k === i;
          return (
            <div
              key={slide.key}
              aria-hidden={!active}
              className={`absolute inset-0 transition-opacity duration-700 ease-out ${active ? 'opacity-100 z-[1]' : 'opacity-0 z-0 pointer-events-none'}`}
            >
              {/* Imagem real com zoom lento (Ken Burns) enquanto ativa */}
              <img
                src={slide.image}
                alt={`MyShow no setor de ${slide.segment} — TV exibindo promoções no ambiente real da loja`}
                className={`absolute inset-0 w-full h-full object-cover ease-linear will-change-transform select-none ${prefersReduced ? '' : 'transition-transform duration-[6000ms]'} ${active && !prefersReduced ? 'scale-[1.12]' : 'scale-100'}`}
                draggable={false}
                loading="lazy"
                decoding="async"
              />
              <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
              <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-black/65 via-transparent to-transparent" />

              <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur text-white/90 text-[11px] px-3 py-1.5 tracking-wider">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> AO VIVO · MYSHOW
              </span>

              <div className="absolute inset-0 flex flex-col justify-end p-7 sm:p-9 md:p-12 max-w-2xl">
                <span className="inline-flex self-start items-center gap-2 rounded-full border border-white/25 bg-white/10 backdrop-blur px-3.5 py-1.5 text-sm font-bold text-white">
                  <span className="text-base leading-none">{slide.emoji}</span> {slide.segment}
                </span>
                <p className="mt-4 font-display text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-white leading-[1.1] drop-shadow-[0_2px_12px_rgba(0,0,0,0.55)]">
                  {slide.line}
                </p>
              </div>
            </div>
          );
        })}

        <button onClick={() => go(-1)} aria-label="Campanha anterior" className="absolute left-3 top-1/2 -translate-y-1/2 z-10 grid place-items-center h-11 w-11 rounded-full bg-black/40 backdrop-blur border border-white/20 text-white hover:bg-black/65 transition-colors">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button onClick={() => go(1)} aria-label="Próxima campanha" className="absolute right-3 top-1/2 -translate-y-1/2 z-10 grid place-items-center h-11 w-11 rounded-full bg-black/40 backdrop-blur border border-white/20 text-white hover:bg-black/65 transition-colors">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Abas de setor (também servem de indicadores) */}
      <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
        {campaignSlides.map((slide, k) => (
          <button
            key={slide.key}
            onClick={() => setI(k)}
            aria-current={k === i}
            aria-label={`Ver ${slide.segment}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-all duration-300 ${k === i ? 'gradient-brand text-white shadow-glow' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
          >
            <span className="leading-none">{slide.emoji}</span> <span className="hidden sm:inline">{slide.segment}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ============================================================================
   PÁGINA
============================================================================ */
export function LandingPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const hasHeroMedia = Boolean(HERO_IMAGE_URL || HERO_VIDEO_URL);

  return (
    <div className="min-h-screen bg-background antialiased selection:bg-brand-200/60" style={{ overflowX: 'clip' }}>
      {/* Banner promo */}
      <div className="gradient-brand text-white text-center py-2.5 px-4 text-sm font-medium relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: 'radial-gradient(600px 80px at 50% 0%, rgba(255,255,255,.35), transparent)' }} />
        <span className="relative">
          <Sparkles className="inline h-4 w-4 mr-1 -mt-0.5" />
          <strong>PROMO DE LANÇAMENTO:</strong> 50% OFF nos 3 primeiros meses — só para os 50 primeiros.{' '}
          <Link to="/pricing" className="underline font-bold hover:opacity-90 underline-offset-4">Garantir meu desconto →</Link>
        </span>
      </div>

      {/* Header */}
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container-app flex items-center justify-between h-16 gap-4">
          <Link to="/" className="flex items-center group shrink-0">
            <MyShowLogoFull height={36} className="transition-transform group-hover:scale-[1.03]" />
          </Link>
          <nav className="flex items-center gap-0.5 text-sm whitespace-nowrap">
            <a href="#beneficios" className="hidden lg:inline-flex px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors">Benefícios</a>
            <a href="#como-funciona" className="hidden lg:inline-flex px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors">Como funciona</a>
            <a href="#segmentos" className="hidden lg:inline-flex px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors">Segmentos</a>
            <Link to="/pricing" className="hidden sm:inline-flex px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors">Planos</Link>
            <Link to="/baixar" className="hidden sm:inline-flex px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors">Baixar</Link>
            <Link to="/login" className="inline-flex px-3 py-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors">Entrar</Link>
            <Button asChild size="sm" variant="gradient" className="btn-gradient-glow ml-2">
              <Link to={SIGNUP}>Criar minha TV grátis</Link>
            </Button>
          </nav>
        </div>
      </header>

      {/* ===================== DESEJO ===================== */}
      {/* HERO */}
      {hasHeroMedia ? (
        /* Layout com foto/vídeo real da loja (full-bleed) + texto HTML por cima */
        <section className="relative isolate overflow-hidden bg-black">
          {HERO_VIDEO_URL ? (
            <video src={HERO_VIDEO_URL} className="absolute inset-0 w-full h-full object-contain object-center md:object-right" autoPlay muted loop playsInline />
          ) : (
            <img
              src={HERO_IMAGE_URL}
              alt="TVs do MyShow exibindo ofertas de carnes dentro de um açougue, com clientes no balcão e o painel de campanhas aberto no notebook e no celular"
              className="absolute inset-0 w-full h-full object-contain object-center md:object-right"
              loading="eager"
              decoding="async"
              {...{ fetchpriority: 'high' }}
            />
          )}
          {/* Gradiente preto da esquerda → direita: preenche a metade vazia (a imagem
              fica contida à direita), mantém o texto legível e funde a borda da foto. */}
          <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/40 md:from-black md:via-black/55 md:to-transparent" />
          <div aria-hidden className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 to-transparent md:hidden" />

          <div className="container-app relative flex items-center min-h-[36rem] md:min-h-[40rem] lg:min-h-[42rem] py-20 md:py-24">
            <div className="w-full min-w-0 max-w-xl lg:max-w-2xl">
              <HeroCopy dark onDemo={() => setDemoOpen(true)} />
            </div>
          </div>
        </section>
      ) : (
        /* Fallback: mockup animado de TV em CSS */
        <section className="relative overflow-hidden">
          <div className="orb bg-brand-300/25 w-[560px] h-[560px] -top-56 -left-56" />
          <div className="orb bg-fuchsia-300/20 w-[420px] h-[420px] top-48 -right-40" />
          <div aria-hidden className="absolute inset-0 bg-dots mask-fade opacity-70" />
          <div className="container-app relative py-14 md:py-24 grid lg:grid-cols-2 gap-12 lg:gap-10 items-center">
            <div><HeroCopy dark={false} onDemo={() => setDemoOpen(true)} /></div>
            <Reveal delay={0.15} className="relative"><HeroTV /></Reveal>
          </div>
        </section>
      )}

      {/* ===================== OPORTUNIDADE FINANCEIRA ===================== */}
      <section className="relative isolate overflow-hidden bg-foreground text-background py-24 md:py-32">
        <div aria-hidden className="absolute inset-0 opacity-50" style={{ background: 'radial-gradient(900px 480px at 18% -5%, rgba(168,85,247,.30), transparent), radial-gradient(760px 460px at 92% 108%, rgba(217,70,239,.22), transparent)' }} />
        <div aria-hidden className="absolute inset-0 bg-grid mask-fade opacity-[0.05]" />
        <div className="container-app relative">
          <Reveal className="text-center max-w-2xl mx-auto mb-14 md:mb-16">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-400/30 bg-brand-500/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-200">
              <DollarSign className="h-3.5 w-3.5" /> Oportunidade
            </span>
            <h2 className="mt-5 font-display text-3xl md:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight leading-[1.05]">
              Quanto vale uma <span className="text-gradient-brand">compra por impulso</span>?
            </h2>
            <p className="mt-5 text-lg md:text-xl text-background/70 leading-relaxed">
              Seu cliente toma dezenas de decisões dentro da loja. Quanto mais visível sua oferta, maiores as chances dela entrar no carrinho.
            </p>
          </Reveal>

          <OportunidadeFinanceira />

          {/* Frase de impacto */}
          <Reveal className="mt-14 md:mt-16 text-center max-w-3xl mx-auto">
            <p className="font-display text-2xl md:text-3xl font-bold tracking-tight leading-snug text-white">
              Uma única oferta bem exibida pode gerar mais resultado do que <span className="text-gradient-brand">semanas de cartazes impressos</span>.
            </p>
          </Reveal>

          {/* Selo + CTA */}
          <Reveal className="mt-12 md:mt-14">
            <div className="relative mx-auto max-w-3xl rounded-3xl border border-white/10 bg-white/[0.04] p-8 md:p-10 text-center overflow-hidden">
              <div aria-hidden className="absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 rounded-full bg-brand-500/20 blur-3xl" />
              <div className="relative">
                <span className="inline-flex items-center gap-2 rounded-full gradient-brand text-white px-4 py-2 text-sm font-bold shadow-glow">
                  📺 Seu cliente compra com os olhos
                </span>
                <p className="mt-5 text-background/75 leading-relaxed max-w-xl mx-auto">
                  O MyShow transforma TVs comuns em ferramentas de venda que destacam promoções exatamente no momento da decisão de compra.
                </p>
                <div className="mt-8 flex flex-col items-center gap-3">
                  <Button asChild size="lg" variant="gradient" className="btn-gradient-glow cta-pulse h-14 px-10 text-base font-bold">
                    <Link to={SIGNUP}>💰 Começar a vender mais <ArrowRight className="h-5 w-5" /></Link>
                  </Button>
                  <span className="text-xs text-background/50">Sem cartão de crédito · sua loja vendendo em minutos</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Números fortes logo abaixo do hero */}
      <section className="bg-background border-b border-border/60">
        <div className="container-app py-8">
          <Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px rounded-2xl bg-border/60 overflow-hidden border border-border/60 shadow-soft">
              {heroMetrics.map((m) => (
                <div key={m.label} className="bg-background/95 px-6 py-6 flex items-center gap-4">
                  <div className="grid place-items-center h-12 w-12 rounded-xl bg-brand-50 text-brand-700 ring-1 ring-brand-100 shrink-0">
                    <m.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-gradient-brand leading-none">{m.kpi}</div>
                    <div className="text-sm text-muted-foreground mt-1.5 leading-snug">{m.label}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SEU CLIENTE COMPRA COM OS OLHOS — desejo / a seção mais importante */}
      <section className="relative py-24 overflow-hidden bg-foreground text-background">
        <div aria-hidden className="absolute inset-0 opacity-40" style={{ background: 'radial-gradient(800px 400px at 20% 0%, rgba(168,85,247,.35), transparent), radial-gradient(700px 400px at 90% 100%, rgba(217,70,239,.25), transparent)' }} />
        <div className="container-app relative">
          <Reveal className="text-center mb-14 max-w-2xl mx-auto">
            <span className="inline-block text-xs uppercase tracking-[0.25em] text-brand-300 font-semibold mb-3">O que muda na loja</span>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
              Seu cliente compra com os <span className="text-gradient-brand">olhos</span>.
            </h2>
            <p className="text-lg text-background/70 mt-4 leading-relaxed">
              Enquanto cartazes passam despercebidos, telas digitais destacam ofertas e influenciam decisões em segundos.
            </p>
          </Reveal>

          {/* Carrossel de setores — troca sozinho, com setas e bolinhas */}
          <Reveal>
            <SegmentCarousel />
          </Reveal>

          <Reveal className="text-center mt-12">
            <Button asChild size="lg" variant="gradient" className="btn-gradient-glow h-12 px-8 shadow-glow">
              <Link to={SIGNUP}>📺 Ver minhas promoções na TV <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ===================== PROBLEMA ===================== */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-muted/30" />
        <div className="container-app relative">
          <Reveal className="text-center mb-14 max-w-2xl mx-auto">
            <SectionTag>O problema</SectionTag>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
              Sua loja está deixando vendas <span className="text-gradient-brand">passarem despercebidas</span>.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6 items-stretch max-w-5xl mx-auto">
            <Reveal>
              <div className="relative h-full rounded-3xl border-2 border-dashed border-border bg-muted/40 p-8 grayscale-[35%]">
                <span className="absolute -top-3 left-6 bg-muted text-muted-foreground text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-border">Sem MyShow</span>
                <div className="mx-auto max-w-[16rem] bg-[#fdfcf7] rounded-sm shadow-md p-5 text-center rotate-[-2deg] border border-amber-900/10 opacity-80">
                  <div className="text-red-700/70 font-black text-2xl tracking-tight" style={{ fontFamily: 'Comic Sans MS, cursive' }}>PROMOÇÃO</div>
                  <div className="mt-3 h-px bg-amber-900/10" />
                  <div className="mt-3 text-foreground/60 text-sm">Arroz 5kg</div>
                  <div className="text-foreground/70 font-bold text-3xl line-through decoration-red-600/60" style={{ fontFamily: 'Comic Sans MS, cursive' }}>R$ 24,90</div>
                  <div className="mt-2 text-[10px] text-muted-foreground italic">escrito à mão · desbotando ao sol</div>
                </div>
                <ul className="mt-8 space-y-3 text-sm">
                  {['Cartaz de papel que ninguém olha', 'Preço errado, desbotado, rasgado', 'Promoção que só muda toda semana', 'Gasto recorrente com gráfica e papel', 'Loja com cara de pequena'].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-muted-foreground">
                      <X className="h-4 w-4 text-red-500 mt-0.5 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="relative h-full rounded-3xl border-2 border-brand-500/40 bg-card p-8 shadow-elev">
                <span className="absolute -top-3 left-6 gradient-brand text-white text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-md">Com MyShow</span>
                <div className="mx-auto max-w-[22rem]">
                  <img
                    src="/tv-oferta-picanha.webp"
                    alt="TV exibindo a Oferta do Dia do MyShow: Picanha Maturada por R$ 39,90"
                    className="w-full h-auto"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <ul className="mt-8 space-y-3 text-sm">
                  {['Tela em movimento que prende o olhar', 'Preço sempre certo, atualizado em segundos', 'Oferta nova quando você quiser, na hora', 'Zero gasto com impressos toda semana', 'Loja com a cara de grande rede'].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-foreground">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ===================== SOLUÇÃO ===================== */}
      {/* Como funciona */}
      <section id="como-funciona" className="relative py-24 overflow-hidden scroll-mt-20">
        <div aria-hidden className="absolute inset-0 bg-grid mask-fade opacity-70" />
        <div className="orb bg-brand-400/30 w-[460px] h-[460px] top-10 -left-32" />
        <div className="container-app relative">
          <Reveal className="text-center mb-14">
            <SectionTag>Como funciona</SectionTag>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">Comece em poucos minutos.</h2>
            <p className="text-lg text-muted-foreground mt-3">Três passos do cadastro à TV vendendo.</p>
          </Reveal>
          <div className="relative">
            <div aria-hidden className="hidden md:block absolute top-6 left-[16.66%] right-[16.66%] step-connector h-0.5" />
            <div className="grid md:grid-cols-3 gap-6 relative">
              {steps.map((step, i) => (
                <Reveal key={step.n} delay={i * 0.1}>
                  <div className="stat-card h-full text-center md:text-left">
                    <div className="mx-auto md:mx-0 grid place-items-center h-12 w-12 rounded-xl gradient-brand text-white font-display font-black text-xl mb-4 shadow-glow">
                      {step.n}
                    </div>
                    <h3 className="font-semibold text-lg tracking-tight flex items-center justify-center md:justify-start gap-2">
                      <step.icon className="h-5 w-5 text-brand-600" /> {step.t}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{step.d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal className="text-center mt-12">
            <Button asChild size="lg" variant="gradient" className="btn-gradient-glow h-12 px-8 shadow-glow">
              <Link to={SIGNUP}>⚡ Configurar minha loja grátis <ArrowRight className="h-4 w-4" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* Demonstração / Painel */}
      <section className="relative py-24 overflow-hidden">
        <div className="orb bg-brand-300/20 w-[520px] h-[520px] top-10 -right-40" />
        <div className="container-app relative">
          <Reveal className="text-center mb-12 max-w-2xl mx-auto">
            <SectionTag>Veja funcionando</SectionTag>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
              Da campanha à TV no ar — <span className="text-gradient-brand">num painel só</span>.
            </h2>
            <p className="text-lg text-muted-foreground mt-3">
              Crie, publique e acompanhe o desempenho de cada tela em tempo real. Do computador ou do celular.
            </p>
          </Reveal>

          <Reveal>
            <div className="relative max-w-5xl mx-auto">
              <div aria-hidden className="absolute -inset-6 bg-gradient-to-br from-brand-500/30 via-fuchsia-500/20 to-purple-700/30 blur-3xl opacity-60 rounded-[2rem]" />
              <button onClick={() => setDemoOpen(true)}
                className="group relative block w-full rounded-3xl overflow-hidden ring-1 ring-border/70 shadow-elev focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-300"
                aria-label="Ver demonstração em movimento">
                <img
                  src="/painel-myshow.webp"
                  alt="Painel do MyShow com o resumo geral — 128 campanhas exibidas, 24.853 impressões e 32 telas ativas, com gráfico de desempenho — ao lado de uma loja com TVs exibindo ofertas de manga, pão e cerveja"
                  className="w-full h-auto block transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                  decoding="async"
                />
                <span aria-hidden className="absolute inset-0 grid place-items-center bg-black/10 group-hover:bg-black/0 transition-colors">
                  <span className="grid place-items-center h-16 w-16 md:h-20 md:w-20 rounded-full bg-white/95 text-brand-700 shadow-glow transition-transform group-hover:scale-110">
                    <Play className="h-7 w-7 md:h-8 md:w-8 ml-1 fill-current" />
                  </span>
                </span>
                <span className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur text-white/90 text-xs px-3 py-1.5 tracking-wider">
                  <Play className="h-3 w-3 fill-current" /> Ver em movimento
                </span>
              </button>
            </div>
          </Reveal>

          <div className="text-center mt-8">
            <Button size="lg" variant="gradient" onClick={() => setDemoOpen(true)} className="btn-gradient-glow h-12 px-8 shadow-glow">
▶ Ver minhas TVs vendendo <Play className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ===================== CAMPANHAS (uso real, não "templates") ===================== */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-muted/30" />
        <div className="orb bg-brand-300/20 w-[480px] h-[480px] -top-24 -left-24" />
        <div className="container-app relative">
          <Reveal className="text-center mb-12 max-w-2xl mx-auto">
            <SectionTag>Campanhas</SectionTag>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">
              Campanhas que <span className="text-gradient-brand">chamam atenção</span> e geram vendas
            </h2>
            <p className="text-lg text-muted-foreground mt-3">
              Escolha um modelo profissional, altere preço e produto em segundos e publique em todas as TVs da sua loja.
            </p>
          </Reveal>

          <Reveal>
            <CampaignCarousel />
          </Reveal>

          {/* Linha de simplicidade — 3 passos */}
          <Reveal className="mt-14">
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-center gap-3 md:gap-2 max-w-4xl mx-auto">
              {[
                { n: '1️⃣', t: 'Escolha um modelo' },
                { n: '2️⃣', t: 'Troque produto e preço' },
                { n: '3️⃣', t: 'Publique em todas as TVs' },
              ].map((step, k, arr) => (
                <Fragment key={step.t}>
                  <div className="flex-1 flex items-center gap-3 rounded-2xl border border-border bg-card px-5 py-4 shadow-soft">
                    <span className="text-2xl leading-none">{step.n}</span>
                    <span className="font-semibold tracking-tight">{step.t}</span>
                  </div>
                  {k < arr.length - 1 && <ArrowRight className="hidden md:block h-5 w-5 text-brand-500 shrink-0" />}
                </Fragment>
              ))}
            </div>
          </Reveal>

          {/* Caixa de destaque — velocidade */}
          <Reveal className="mt-8 text-center">
            <div className="inline-flex items-center gap-2.5 rounded-2xl gradient-brand text-white px-6 py-4 shadow-glow font-display text-lg md:text-xl font-bold">
              <Zap className="h-5 w-5 shrink-0" /> Em menos de 1 minuto sua campanha está no ar.
            </div>
          </Reveal>

          {/* CTA */}
          <Reveal className="mt-8 text-center">
            <Button asChild size="lg" variant="gradient" className="btn-gradient-glow cta-pulse h-14 px-10 text-base font-bold shadow-glow">
              <Link to={SIGNUP}>🚀 Criar minha primeira campanha <ArrowRight className="h-5 w-5" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ===================== BENEFÍCIOS ===================== */}
      <section id="beneficios" className="container-app py-24 scroll-mt-20">
        <Reveal className="text-center mb-14">
          <SectionTag>Por que MyShow</SectionTag>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">Por que empresas escolhem o MyShow</h2>
          <p className="text-lg text-muted-foreground mt-3">Não vendemos software. Vendemos mais vendas na sua loja.</p>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {benefits.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.06}>
              <div className="stat-card h-full">
                <div className="grid place-items-center h-12 w-12 rounded-xl bg-brand-600 text-white shadow-sm ring-1 ring-brand-700/10">
                  <b.icon className="h-6 w-6" strokeWidth={2} />
                </div>
                <h3 className="mt-5 text-lg font-semibold tracking-tight">{b.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ===================== COMPARATIVO ===================== */}
      <section className="relative isolate overflow-hidden bg-foreground text-background py-24 md:py-28">
        <div aria-hidden className="absolute inset-0 opacity-50" style={{ background: 'radial-gradient(820px 460px at 85% -5%, rgba(168,85,247,.30), transparent), radial-gradient(680px 420px at 10% 105%, rgba(217,70,239,.18), transparent)' }} />
        <div aria-hidden className="absolute inset-0 bg-grid mask-fade opacity-[0.05]" />
        <div className="container-app relative">
          <Reveal className="text-center mb-14 md:mb-16 max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-400/30 bg-brand-500/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-200">
              Comparativo
            </span>
            <h2 className="mt-5 font-display text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.05]">
              Por que escolher o <span className="text-gradient-brand">MyShow</span>?
            </h2>
            <p className="mt-5 text-lg md:text-xl text-background/70 leading-relaxed">
              Mais controle, menos trabalho e mais impacto para suas promoções.
            </p>
          </Reveal>

          <div className="relative grid md:grid-cols-2 gap-5 md:gap-0 items-stretch max-w-5xl mx-auto">
            {/* Divisor "VS" central (desktop) */}
            <div aria-hidden className="hidden md:grid place-items-center absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 h-12 w-12 rounded-full bg-foreground border border-white/15 text-background/60 font-display font-black text-sm shadow-xl">
              VS
            </div>

            {/* Métodos tradicionais — discreto */}
            <Reveal>
              <div className="relative h-full rounded-3xl md:rounded-r-none border border-white/10 bg-white/[0.03] p-8 md:p-9 md:pr-12">
                <div className="flex items-center gap-3 pb-5 border-b border-white/10">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-white/5 text-background/50 border border-white/10"><X className="h-6 w-6" /></span>
                  <div>
                    <div className="font-display text-xl font-bold tracking-tight text-background/70">Métodos tradicionais</div>
                    <div className="text-xs text-background/40">Trabalhoso, manual e sem impacto</div>
                  </div>
                </div>
                <ul className="mt-2">
                  {['Atualizações manuais', 'Pendrive e PowerPoint', 'Cartazes impressos', 'Comunicação desatualizada', 'Difícil gerenciar várias lojas', 'Baixo impacto visual'].map((t) => (
                    <li key={t} className="flex items-center gap-3 text-background/55 py-3.5 border-b border-white/5 last:border-0">
                      <X className="h-5 w-5 text-red-400/80 shrink-0" /> <span className="line-through decoration-white/15">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {/* MyShow — destacado, elevado e iluminado */}
            <Reveal delay={0.1}>
              <div className="relative h-full rounded-3xl md:rounded-l-none border border-brand-400/50 bg-gradient-to-br from-brand-500/[0.18] to-fuchsia-500/[0.06] p-8 md:p-9 md:pl-12 shadow-glow md:-translate-y-3 md:scale-[1.02] overflow-hidden z-10">
                <div aria-hidden className="absolute inset-x-0 top-0 h-1.5 gradient-brand" />
                <span className="absolute top-4 right-6 inline-flex items-center gap-1.5 gradient-brand text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-md">
                  <Sparkles className="h-3 w-3" /> Recomendado
                </span>
                <div className="flex items-center gap-3 pb-5 border-b border-white/10">
                  <span className="grid place-items-center h-11 w-11 rounded-xl gradient-brand text-white shadow-glow"><Tv className="h-6 w-6" /></span>
                  <div>
                    <div className="font-display text-xl font-extrabold tracking-tight text-white">MyShow</div>
                    <div className="text-xs text-brand-200">Automático, centralizado e profissional</div>
                  </div>
                </div>
                <ul className="mt-2">
                  {['Atualização automática', 'Integração com ERP', 'Gestão centralizada', 'Promoções em tempo real', 'Controle de múltiplas lojas', 'Comunicação moderna e profissional'].map((t) => (
                    <li key={t} className="flex items-center gap-3 text-white font-medium py-3.5 border-b border-white/5 last:border-0">
                      <span className="grid place-items-center h-5 w-5 rounded-full bg-emerald-500/20 shrink-0"><CheckCircle2 className="h-4 w-4 text-emerald-400" /></span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal className="text-center mt-12 md:mt-14">
            <Button asChild size="lg" variant="gradient" className="btn-gradient-glow cta-pulse h-14 px-10 text-base font-bold shadow-glow">
              <Link to={SIGNUP}>🚀 Criar minha primeira TV agora <ArrowRight className="h-5 w-5" /></Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* ===================== PROVA ===================== */}
      {/* Segmentos */}
      <section id="segmentos" className="container-app py-24 scroll-mt-20">
        <Reveal className="text-center mb-14">
          <SectionTag>Segmentos</SectionTag>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">Ideal para diversos tipos de negócios.</h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {segments.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.04}>
              {/* Estruturado para futuras landing pages nichadas: trocar para /para/${s.slug} */}
              <Link to={`${SIGNUP}?seg=${s.slug}`} className="stat-card text-center h-full group block">
                <div className="mx-auto h-14 w-14 grid place-items-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 ring-1 ring-brand-200/70 mb-3 transition-transform group-hover:scale-110">
                  <s.icon className="h-7 w-7 text-brand-700" />
                </div>
                <div className="font-semibold tracking-tight">{s.name}</div>
                <div className="text-xs text-muted-foreground mt-1">{s.desc}</div>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-brand-700 opacity-0 group-hover:opacity-100 transition-opacity">
                  Começar a vender <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Prova social */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-muted/30" />
        <div className="container-app relative">
          <Reveal className="text-center mb-12">
            <SectionTag>Prova social</SectionTag>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">Empresas que já utilizam o MyShow.</h2>
          </Reveal>

          {/* Credibilidade em números */}
          <Reveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px rounded-2xl bg-border/60 overflow-hidden border border-border/60 shadow-soft mb-12">
              {credStats.map((s) => (
                <div key={s.label} className="bg-background/95 px-6 py-5 text-center">
                  <div className="font-display text-2xl md:text-3xl font-extrabold tracking-tight text-foreground">{s.kpi}</div>
                  <div className="text-xs md:text-sm text-muted-foreground mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Faixa de logos — PLACEHOLDER. Trocar por <img> dos logos reais autorizados. */}
          <Reveal>
            <div className="marquee mb-12">
              <div className="marquee-track">
                {[...segments, ...segments].map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-muted-foreground/70 text-sm font-semibold whitespace-nowrap">
                    <s.icon className="h-5 w-5" /> <span>{s.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure className="stat-card h-full relative">
                  <Quote className="absolute top-5 right-5 h-7 w-7 text-brand-200" />
                  <div className="text-amber-400 text-lg leading-none mb-3" aria-label="5 estrelas">★★★★★</div>
                  <blockquote className="text-foreground leading-relaxed">"{t.quote}"</blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-border/60">
                    <div className="font-semibold text-sm">{t.author}</div>
                    <div className="text-xs text-muted-foreground">{t.region}</div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <Reveal>
            <p className="text-center text-xs text-muted-foreground mt-6">
              Depoimentos ilustrativos — substituir por casos reais aprovados antes da publicação.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ===================== URGÊNCIA / AÇÃO IMEDIATA ===================== */}
      <section className="relative py-24 md:py-28 overflow-hidden">
        <div aria-hidden className="absolute inset-0 bg-grid mask-fade opacity-60" />
        <div className="orb bg-brand-300/25 w-[460px] h-[460px] -top-24 left-1/2 -translate-x-1/2" />
        <div className="container-app relative">
          <Reveal className="text-center max-w-3xl mx-auto">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-200 bg-brand-50/80 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-brand-700">
              <Clock className="h-3.5 w-3.5" /> Pronto em ~15 minutos
            </span>
            <h2 className="mt-6 font-display text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.08]">
              Sua próxima promoção pode estar no ar em <span className="text-gradient-brand">menos de 15 minutos</span>.
            </h2>
            <p className="mt-5 text-lg md:text-xl text-muted-foreground leading-relaxed">
              Crie sua conta, conecte sua primeira TV e publique campanhas em poucos minutos.
            </p>
          </Reveal>

          {/* 3 motivos para não adiar — velocidade, equipamento, custo zero */}
          <div className="mt-14 grid sm:grid-cols-3 gap-5 md:gap-6 max-w-5xl mx-auto">
            {[
              { emoji: '⚡', title: 'Configuração rápida', desc: 'Sem necessidade de equipe técnica.', glow: 'rgba(168,85,247,.35)' },
              { emoji: '📺', title: 'Aproveite as TVs que você já possui', desc: 'Não é preciso investir em novos equipamentos.', glow: 'rgba(217,70,239,.32)' },
              { emoji: '🚀', title: 'Comece gratuitamente', desc: 'Teste o MyShow e veja suas promoções funcionando hoje.', glow: 'rgba(245,158,11,.32)' },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 0.12}>
                <div className="stat-card h-full text-center sm:text-left">
                  <div className="mx-auto sm:mx-0 grid place-items-center h-14 w-14 rounded-2xl text-2xl bg-gradient-to-br from-brand-100 to-brand-50 ring-1 ring-brand-200/70 transition-transform duration-300 group-hover:scale-110"
                    style={{ boxShadow: `0 12px 36px -12px ${c.glow}` }}>
                    <span>{c.emoji}</span>
                  </div>
                  <h3 className="mt-5 text-lg font-semibold tracking-tight">{c.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* CTA — sem fricção, sem motivo pra adiar */}
          <Reveal className="mt-12 text-center">
            <Button asChild size="lg" variant="gradient" className="btn-gradient-glow cta-pulse h-14 px-10 text-base font-bold shadow-glow">
              <Link to={SIGNUP}>🚀 Criar minha primeira TV agora <ArrowRight className="h-5 w-5" /></Link>
            </Button>
            <p className="mt-5 text-sm text-muted-foreground flex flex-wrap justify-center gap-x-5 gap-y-1.5">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Sem cartão de crédito</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Sem instalação complicada</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-500" /> Suas promoções no ar hoje</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-app py-24 max-w-3xl">
        <Reveal className="text-center mb-12">
          <SectionTag>Dúvidas</SectionTag>
          <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight">Perguntas frequentes</h2>
        </Reveal>
        <div className="space-y-3">
          {faqs.map((f) => <Reveal key={f.q}><FaqItem {...f} /></Reveal>)}
        </div>
        <Reveal className="text-center mt-10">
          <p className="text-muted-foreground text-sm mb-4">Ainda com dúvida? Fale com a gente agora.</p>
          <Button asChild size="lg" variant="outline" className="h-12 px-7 border-border hover:border-foreground/30">
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer"><MessageCircle className="h-4 w-4" /> Falar no WhatsApp</a>
          </Button>
        </Reveal>
      </section>

      {/* ===================== CADASTRO ===================== */}
      <section className="container-app py-24">
        <Reveal>
          <div className="relative rounded-3xl gradient-brand p-12 md:p-20 text-white text-center overflow-hidden shadow-glow">
            <div aria-hidden className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(800px 200px at 20% 0%, rgba(255,255,255,.35), transparent), radial-gradient(600px 200px at 80% 100%, rgba(255,255,255,.25), transparent)' }} />
            <div aria-hidden className="absolute inset-0 bg-dots opacity-15" />
            <h2 className="relative font-display text-3xl md:text-5xl font-bold tracking-tight max-w-3xl mx-auto">
              Transforme suas TVs em ferramentas de vendas hoje mesmo.
            </h2>
            <p className="relative mt-5 text-white/90 max-w-xl mx-auto text-lg">
              Comece gratuitamente e publique sua primeira campanha em poucos minutos.
            </p>
            <div className="relative mt-10 flex flex-col items-center gap-4">
              <Button asChild size="lg" variant="secondary" className="cta-pulse px-10 text-lg font-bold bg-white text-brand-700 hover:bg-white/95 shadow-elev" style={{ height: '3.75rem' }}>
                <Link to={SIGNUP}>🚀 Transformar minhas TVs em vendedores</Link>
              </Button>
              <span className="text-white/80 text-sm flex flex-wrap justify-center gap-x-5 gap-y-1">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Sem cartão</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Sem fidelidade</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Cancele quando quiser</span>
              </span>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border/60 py-14 bg-muted/20">
        <div className="container-app grid md:grid-cols-4 gap-10 text-sm">
          <div>
            <MyShowLogoFull height={32} />
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Transforme qualquer TV em um vendedor digital. Mais de 24 anos de tecnologia para o varejo.
            </p>
            <a href="https://visualsoftware.inf.br" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <span>Powered by</span>
              <img src="https://visualsoftware.inf.br/assets/logo_visual_software-BBverSN6.webp" alt="Visual Software" className="h-6 w-auto" loading="lazy" />
            </a>
          </div>
          <div>
            <h4 className="font-semibold mb-3 tracking-tight">Produto</h4>
            <ul className="space-y-2 text-muted-foreground">
              <li><a href="#beneficios" className="hover:text-foreground transition-colors">Benefícios</a></li>
              <li><a href="#como-funciona" className="hover:text-foreground transition-colors">Como funciona</a></li>
              <li><Link to="/pricing" className="hover:text-foreground transition-colors">Planos</Link></li>
              <li><a href="#segmentos" className="hover:text-foreground transition-colors">Segmentos</a></li>
              <li><Link to="/baixar" className="hover:text-foreground transition-colors">Baixar</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-3 tracking-tight">Contato</h4>
            <ul className="space-y-2 text-muted-foreground">
              <li>📞 (46) 9982-6101</li>
              <li>✉️ comercial@visualsoftware.inf.br</li>
              <li><a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">💬 WhatsApp</a></li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-3 tracking-tight">Unidades</h4>
            <ul className="space-y-2 text-muted-foreground">
              <li>Quedas do Iguaçu — PR</li>
              <li>Cascavel — PR</li>
              <li>Maringá — PR</li>
            </ul>
          </div>
        </div>
        <div className="container-app mt-10 pt-8 border-t border-border/60 text-xs text-muted-foreground flex flex-wrap justify-between gap-2">
          <span>© {new Date().getFullYear()} Visual Software · MyShow é um produto Visual Software</span>
          <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> LGPD compliant</span>
        </div>
      </footer>

      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}
